// src/PostForm.js
// Форма создания и редактирования поста. Одна на админ-панель и на кнопку
// «Создать пост» в ленте, чтобы оба места вели себя одинаково.

import React, { useState, useEffect, useId } from 'react';
import { api } from './api';
import RichTextEditor from './RichTextEditor';
import './AdminUsers.css';
import './FileUpload.css';

// toISOString() возвращает время по Гринвичу, а поле datetime-local
// показывает его как местное — из-за этого отметка публикации уезжала
// назад на разницу часовых поясов (в Москве на 3 часа).
export const toLocalInputValue = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const initialFormData = (post, initialSection, initialSubcategory) => {
  if (post) {
    return {
      title: post.title || '',
      content: post.content || '',
      section: post.section?.toString() || '',
      subcategory: post.subcategory?.toString() || '',
      pinned: post.pinned || false,
      is_public: post.is_public !== false,
      is_important: post.is_important || false,
      // У существующего поста время не должно меняться само собой
      publish_mode: 'custom',
      created_at: toLocalInputValue(post.created_at ? new Date(post.created_at) : new Date()),
    };
  }
  return {
    title: '',
    content: '',
    section: initialSection ? String(initialSection) : '',
    subcategory: initialSection && initialSubcategory ? String(initialSubcategory) : '',
    pinned: false,
    is_public: true,
    is_important: false,
    // 'now' — отметка ставится в момент отправки, 'custom' — берётся из поля
    publish_mode: 'now',
    created_at: toLocalInputValue(new Date()),
  };
};

const initialPollData = (post) => (
  post?.poll
    ? {
      hasPoll: true,
      question: post.poll.question || '',
      multiple: post.poll.multiple || false,
      options: post.poll.options?.map(opt => opt.text) || ['', ''],
    }
    : { hasPoll: false, question: '', multiple: false, options: ['', ''] }
);

export default function PostForm({
  post = null,
  // Разделы можно передать готовыми (админ-панель их уже загрузила),
  // иначе форма загрузит их сама
  sections: sectionsProp,
  initialSection = '',
  initialSubcategory = '',
  onCancel,
  onSaved,
}) {
  const editingPost = post;
  const fileInputId = useId();
  const [loadedSections, setLoadedSections] = useState([]);
  const sections = sectionsProp || loadedSections;
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(() => initialFormData(post, initialSection, initialSubcategory));
  const [pollData, setPollData] = useState(() => initialPollData(post));
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState(post?.attachments || []);
  const [attachmentDisplayNames, setAttachmentDisplayNames] = useState(() => {
    const displayNames = {};
    (post?.attachments || []).forEach(att => {
      if (att.id) {
        displayNames[att.id] = att.display_name || '';
      }
    });
    return displayNames;
  });

  useEffect(() => {
    if (sectionsProp) return undefined;
    let cancelled = false;
    api.getSections()
      .then((data) => { if (!cancelled) setLoadedSections(data); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [sectionsProp]);

  const subcategories = [];
  for (const section of sections) {
    if (section.subcategories) {
      subcategories.push(...section.subcategories);
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      // Подкатегория другого раздела осталась бы выбранной и ушла бы на сервер
      ...(name === 'section' && value !== prev.section ? { subcategory: '' } : {}),
    }));
  };

  const handlePollChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPollData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePollOptionChange = (index, value) => {
    setPollData(prev => {
      const newOptions = [...prev.options];
      newOptions[index] = value;
      return { ...prev, options: newOptions };
    });
  };

  const addPollOption = () => {
    setPollData(prev => ({
      ...prev,
      options: [...prev.options, ''],
    }));
  };

  const removePollOption = (index) => {
    setPollData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    // Удаляем display_name для удаленного файла
    setAttachmentDisplayNames(prev => {
      const newNames = { ...prev };
      delete newNames[`new_${index}`];
      return newNames;
    });
  };

  const removeExistingAttachment = (attachmentId) => {
    setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId));
    // Удаляем display_name для удаленного файла
    setAttachmentDisplayNames(prev => {
      const newNames = { ...prev };
      delete newNames[attachmentId];
      return newNames;
    });
  };

  const updateAttachmentDisplayName = (attachmentId, displayName) => {
    setAttachmentDisplayNames(prev => ({
      ...prev,
      [attachmentId]: displayName
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Повторное нажатие, пока загружаются файлы, создало бы второй пост
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    try {
      // Создаем FormData для поддержки файлов
      const formDataToSend = new FormData();
      
      // Добавляем основные поля поста
      formDataToSend.append('title', formData.title);
      formDataToSend.append('content', formData.content);
      if (formData.section) formDataToSend.append('section', formData.section);
      if (formData.subcategory) formDataToSend.append('subcategory', formData.subcategory);
      // FormData приводит значение к строке: undefined превратилось бы
      // в "undefined", и сервер отклонил бы поле как не-булево.
      formDataToSend.append('pinned', formData.pinned ? 'true' : 'false');
      formDataToSend.append('is_public', formData.is_public ? 'true' : 'false');
      formDataToSend.append('is_important', formData.is_important ? 'true' : 'false');
      if (formData.publish_mode === 'custom' && formData.created_at) {
        // Значение поля трактуется как местное время и переводится в UTC
        formDataToSend.append('created_at', new Date(formData.created_at).toISOString());
      } else {
        // «Сейчас» — отметка ставится в момент нажатия «Создать»,
        // а не когда форму открыли
        formDataToSend.append('created_at', new Date().toISOString());
      }

      // Добавляем новые файлы
      attachments.forEach((file, index) => {
        formDataToSend.append(`attachment_${index}`, file);
        // Добавляем display_name для нового файла, если указан
        const displayName = attachmentDisplayNames[`new_${index}`] || '';
        if (displayName) {
          formDataToSend.append(`attachment_display_name_${index}`, displayName);
        }
      });

      // Если редактируем пост, добавляем список удаленных файлов
      if (editingPost) {
        const allExistingIds = (editingPost.attachments || []).map(att => att.id);
        const remainingIds = existingAttachments.map(att => att.id);
        const deletedIds = allExistingIds.filter(id => !remainingIds.includes(id));
        if (deletedIds.length > 0) {
          formDataToSend.append('delete_attachments', JSON.stringify(deletedIds));
        }

        // Добавляем обновления display_name для существующих файлов
        const attachmentUpdates = {};
        existingAttachments.forEach(att => {
          if (att.id && attachmentDisplayNames[att.id] !== undefined) {
            const currentDisplayName = attachmentDisplayNames[att.id] || '';
            const originalDisplayName = (editingPost.attachments || []).find(a => a.id === att.id)?.display_name || '';
            if (currentDisplayName !== originalDisplayName) {
              attachmentUpdates[att.id] = { display_name: currentDisplayName };
            }
          }
        });
        if (Object.keys(attachmentUpdates).length > 0) {
          formDataToSend.append('attachment_updates', JSON.stringify(attachmentUpdates));
        }
      }

      // Добавляем данные опроса, если он есть
      if (pollData.hasPoll && pollData.question && pollData.question.trim()) {
        formDataToSend.append('poll_question', pollData.question);
        formDataToSend.append('poll_multiple', pollData.multiple ? 'true' : 'false');
        const validOptions = pollData.options.filter(opt => opt && opt.trim());
        validOptions.forEach((option, index) => {
          formDataToSend.append(`poll_option_${index}`, option.trim());
        });
      } else if (editingPost && pollData.hasPoll === false) {
        // Если опрос был удален при редактировании
        formDataToSend.append('poll_question', '');
      }
      
      if (editingPost) {
        await api.updatePost(editingPost.id, formDataToSend);
      } else {
        await api.createPost(formDataToSend);
      }

      const section = sections.find((item) => String(item.id) === String(formData.section));
      const subcategory = subcategories.find((item) => String(item.id) === String(formData.subcategory));
      if (onSaved) {
        onSaved({
          created: !editingPost,
          title: formData.title,
          sectionTitle: section ? section.title : '',
          subcategoryTitle: subcategory ? subcategory.title : '',
        });
      }
    } catch (err) {
      // Ошибку показываем в самой форме: раньше она выводилась над
      // таблицей постов и оставалась незамеченной за открытой формой
      setError(err.message);
      setSubmitting(false);
    }
  };

  const filteredSubcategories = formData.section
    ? subcategories.filter(sub => sub.section === parseInt(formData.section))
    : [];

  return (
    <div className="admin-form-overlay">
      <form className="admin-form" onSubmit={handleSubmit}>
        <div className="form-header">
          <h4>{editingPost ? 'Редактировать пост' : 'Создать пост'}</h4>
          <button type="button" className="form-close" onClick={onCancel} aria-label="Закрыть">×</button>
        </div>

        {error && (
          <div className="admin-error" role="alert">
            {error}
          </div>
        )}

        <div className="form-group">
          <label>Заголовок *</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Содержание *</label>
          <RichTextEditor
            value={formData.content}
            onChange={(html) => setFormData((prev) => ({ ...prev, content: html }))}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Раздел</label>
            <select name="section" value={formData.section} onChange={handleInputChange}>
              <option value="">Выберите раздел</option>
              {sections.map(section => (
                <option key={section.id} value={section.id}>{section.title}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Подкатегория</label>
            <select name="subcategory" value={formData.subcategory} onChange={handleInputChange} disabled={!formData.section}>
              <option value="">Выберите подкатегорию</option>
              {filteredSubcategories.map(sub => (
                <option key={sub.id} value={sub.id}>{sub.title}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="pinned"
                checked={formData.pinned}
                onChange={handleInputChange}
              />
              Закрепить
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_public"
                checked={formData.is_public}
                onChange={handleInputChange}
              />
              Публичный
            </label>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_important"
                checked={formData.is_important}
                onChange={handleInputChange}
              />
              Важно
            </label>
          </div>
        </div>

        <div className="form-group">
          <label>Время публикации</label>

          <div className="publish-mode">
            <label className="publish-mode-option">
              <input
                type="radio"
                name="publish_mode"
                value="now"
                checked={formData.publish_mode === 'now'}
                onChange={handleInputChange}
              />
              <span>Сейчас</span>
            </label>
            <label className="publish-mode-option">
              <input
                type="radio"
                name="publish_mode"
                value="custom"
                checked={formData.publish_mode === 'custom'}
                onChange={(e) => {
                  // Переходя к ручному вводу, подставляем текущее время,
                  // чтобы поле не оказалось пустым
                  const value = formData.created_at || toLocalInputValue(new Date());
                  setFormData((prev) => ({ ...prev, publish_mode: e.target.value, created_at: value }));
                }}
              />
              <span>Указать дату и время</span>
            </label>
          </div>

          {formData.publish_mode === 'custom' ? (
            <>
              <input
                type="datetime-local"
                name="created_at"
                value={formData.created_at}
                onChange={handleInputChange}
                className="publish-datetime"
              />
              <p style={{ marginTop: '6px', fontSize: '12px', color: 'var(--eu-text-muted)' }}>
                Пост появится в ленте с указанными датой и временем
              </p>
            </>
          ) : (
            <p style={{ marginTop: '6px', fontSize: '12px', color: 'var(--eu-text-muted)' }}>
              Время будет проставлено в момент нажатия «{editingPost ? 'Сохранить' : 'Создать'}»
            </p>
          )}
        </div>

        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="hasPoll"
              checked={pollData.hasPoll}
              onChange={handlePollChange}
            />
            Добавить опрос
          </label>
        </div>

        {pollData.hasPoll && (
          <div className="poll-form">
            <div className="form-group">
              <label>Вопрос опроса *</label>
              <input
                type="text"
                name="question"
                value={pollData.question}
                onChange={handlePollChange}
                placeholder="Введите вопрос"
                required={pollData.hasPoll}
              />
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="multiple"
                  checked={pollData.multiple}
                  onChange={handlePollChange}
                />
                Разрешить множественный выбор
              </label>
            </div>

            <div className="form-group">
              <label>Варианты ответов *</label>
              {pollData.options.map((option, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => handlePollOptionChange(index, e.target.value)}
                    placeholder={`Вариант ${index + 1}`}
                    required={pollData.hasPoll}
                    style={{ flex: 1 }}
                  />
                  {pollData.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removePollOption(index)}
                      style={{
                        padding: '8px 12px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addPollOption}
                style={{
                  padding: '8px 16px',
                  background: 'var(--eu-accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  marginTop: '8px',
                }}
              >
                + Добавить вариант
              </button>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Прикрепленные файлы</label>
          <div className="file-upload-wrapper">
            <input
              type="file"
              id={fileInputId}
              className="file-upload-input"
              multiple
              onChange={handleFileChange}
            />
            <label htmlFor={fileInputId} className="file-upload-button">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <span>Выбрать файлы</span>
            </label>
          </div>
          {/* Существующие файлы (только при редактировании) */}
          {editingPost && existingAttachments.length > 0 && (
            <div className="file-upload-list" style={{ marginBottom: '16px' }}>
              <div style={{ marginBottom: '8px', fontWeight: 600, color: 'var(--eu-text)' }}>Существующие файлы:</div>
              {existingAttachments.map((attachment) => (
                <div key={attachment.id} className="file-upload-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <div className="file-upload-item-name">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                        <polyline points="13 2 13 9 20 9"></polyline>
                      </svg>
                      <span>{attachment.file_name || 'Файл'}</span>
                    </div>
                    <button
                      type="button"
                      className="file-upload-item-remove"
                      onClick={() => removeExistingAttachment(attachment.id)}
                    >
                      Удалить
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Отображаемое название (необязательно)"
                    value={attachmentDisplayNames[attachment.id] || ''}
                    onChange={(e) => updateAttachmentDisplayName(attachment.id, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--eu-border)',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          {/* Новые файлы */}
          {attachments.length > 0 && (
            <div className="file-upload-list">
              <div style={{ marginBottom: '8px', fontWeight: 600, color: 'var(--eu-text)' }}>Новые файлы:</div>
              {attachments.map((file, index) => (
                <div key={index} className="file-upload-item" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <div className="file-upload-item-name">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                        <polyline points="13 2 13 9 20 9"></polyline>
                      </svg>
                      <span>{file.name}</span>
                    </div>
                    <button
                      type="button"
                      className="file-upload-item-remove"
                      onClick={() => removeAttachment(index)}
                    >
                      Удалить
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Отображаемое название (необязательно)"
                    value={attachmentDisplayNames[`new_${index}`] || ''}
                    onChange={(e) => updateAttachmentDisplayName(`new_${index}`, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--eu-border)',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" className="admin-button admin-button-cancel" onClick={onCancel}>
            Отмена
          </button>
          <button type="submit" className="admin-button admin-button-save" disabled={submitting}>
            {submitting ? 'Сохранение...' : (editingPost ? 'Сохранить' : 'Создать')}
          </button>
        </div>
      </form>
    </div>
  );
}
